/**
 * ================================================================
 * REDUX SLICE: Session Management
 * ================================================================
 *
 * @slice            userSlice
 * @description      Complete session state management - authentication, user data, theme
 * @purpose          Single source of truth for all session-related data
 *
 * @stateManagement
 * - Authentication: Token, user data, session status
 * - User preferences: Theme, settings, preferences
 * - Session metadata: Login time, session duration, last activity
 *
 * @persistence
 * - Redux state is the primary source of truth
 * - Storage synchronization handled by middleware/hydration
 * - No direct storage access from components
 *
 * ================================================================
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { User } from '@/types/auth/auth';
import type { UserPreferences, UserState } from '@/types/session';

const initialState: UserState = {
  session: {
    authToken: null,
    user: null,
    isAuthenticated: false,
    loginTimestamp: null,
    lastActivityTimestamp: Date.now(),
    sessionExpiryTime: null,
  },
  preferences: {
    sidebarCollapsed: false,
    notifications: true,
    autoLogout: true,
    sessionTimeout: 30, // 30 minutes default
  },
  isLoading: false,
  error: null,
  isHydrated: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // ================================================================
    // SESSION MANAGEMENT ACTIONS
    // ================================================================

    /**
     * Hydrate state from storage during app initialization
     */
    hydrateSession: (state, action: PayloadAction<Partial<UserState>>) => {
      const { session, preferences } = action.payload;

      if (session) {
        state.session = { ...state.session, ...session };
      }
      if (preferences) {
        state.preferences = { ...state.preferences, ...preferences };
      }

      state.isHydrated = true;
      state.error = null;
    },

    /**
     * Set complete authentication session
     */
    setSession: (
      state,
      action: PayloadAction<{ authToken: string; user: User; expiryTime?: number }>,
    ) => {
      const { authToken, user, expiryTime } = action.payload;
      const now = Date.now();

      state.session.authToken = authToken;
      state.session.user = user;
      state.session.isAuthenticated = true;
      state.session.loginTimestamp = now;
      state.session.lastActivityTimestamp = now;
      state.session.sessionExpiryTime =
        expiryTime || now + state.preferences.sessionTimeout * 60 * 1000;
      state.error = null;
    },

    /**
     * Clear complete session - logout
     */
    clearSession: (state) => {
      state.session = {
        authToken: null,
        user: null,
        isAuthenticated: false,
        loginTimestamp: null,
        lastActivityTimestamp: Date.now(),
        sessionExpiryTime: null,
      };
      state.error = null;
    },

    /**
     * Update user activity timestamp
     */
    updateActivity: (state) => {
      state.session.lastActivityTimestamp = Date.now();
    },

    /**
     * Update session token (for token refresh)
     */
    updateToken: (state, action: PayloadAction<string>) => {
      state.session.authToken = action.payload;
      state.session.lastActivityTimestamp = Date.now();
    },

    // ================================================================
    // USER PREFERENCES ACTIONS
    // ================================================================

    /**
     * Update user preferences
     */
    updatePreferences: (state, action: PayloadAction<Partial<UserPreferences>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },

    // ================================================================
    // UI STATE ACTIONS
    // ================================================================

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Clear error state
     */
    clearError: (state) => {
      state.error = null;
    },
  },
});

// ================================================================
// EXPORTED ACTIONS
// ================================================================

export const {
  hydrateSession,
  setSession,
  clearSession,
  updateActivity,
  updateToken,
  updatePreferences,
  setLoading,
  setError,
  clearError,
} = userSlice.actions;

// ================================================================
// SELECTORS
// ================================================================

export const selectSession = (state: { user: UserState }) => state.user.session;
export const selectAuthToken = (state: { user: UserState }) => state.user.session.authToken;
export const selectUser = (state: { user: UserState }) => state.user.session.user;
export const selectIsAuthenticated = (state: { user: UserState }) =>
  state.user.session.isAuthenticated;
export const selectPreferences = (state: { user: UserState }) => state.user.preferences;
export const selectIsLoading = (state: { user: UserState }) => state.user.isLoading;
export const selectError = (state: { user: UserState }) => state.user.error;
export const selectIsHydrated = (state: { user: UserState }) => state.user.isHydrated;

export default userSlice.reducer;
